import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileIcon } from '../../components/icons/ChatCategoryIcons.js';
import { getScoringHistory, getScoringHistoryExportData, getScoringRecord, deleteScoringRecord, updateScoringResult, updateScoringRecordMeta, getScoringFilePreview, refineScoringResult } from '../../api/scoringApi';
import { getApiUrl } from '../../utils/apiConfig';
import { trackEvent } from '../../utils/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import { generateStudentPdf, generateBulkSinglePdf, printPdfBlob } from '../../utils/scoringPdfGenerator';
import {
  getScoringStudentName,
  mergeCachedAnswerTextsWithResults,
  mergeScoringResultMeta,
  normalizeStudentNameForMatch,
} from './scoringResultMeta';
import { buildScoringHistoryExportRows } from './scoringExportData';
import { buildSaenggibuHandoffState, SCORING_HANDOFF_MAX_RECORDS } from './scoringSaenggibuHandoff';
import { loadMultiRecordHandoff } from './scoringMultiHandoff';
import { buildItemScoreEdit } from './scoringResultEdits';
import { writeRecognitionWorkbook } from '../../utils/recognitionReview';
import ScoringReviewWorkspace, { ScoringCriterionChecks, ScoringDistributionNotice, ScoringReviewLauncher } from './ScoringReviewWorkspace';
import { getGradeOnlyDistribution, getGradeOnlySummary, isGradeOnlyResult, isGradeOnlyRubric } from './scoringGradeMode';
// PDF: html2canvas + jsPDF (scoringPdfGenerator에서 사용)
import '../saenggibu/SaenggibuHistory.css';
import './ScoringPage.css';
import './ScoringHistory.css';

const buildPdfPreviewSrc = (url, page) => {
  const pageNumber = Number.parseInt(page, 10);
  const pageParam = Number.isFinite(pageNumber) && pageNumber > 0 ? `page=${pageNumber}&` : '';
  return `${url}#${pageParam}toolbar=0&navpanes=0&scrollbars=1&statusbar=0&messages=0&pagemode=none&view=FitH`;
};

// ── 미리보기 fallback 메시지 헬퍼 (HWP/Office 문서 등 브라우저 직접 렌더 불가 케이스용) ──
function getHistoryFallbackHint(filename, fileType) {
  const lower = (filename || '').toLowerCase();
  const ft = (fileType || '').toLowerCase();
  if (lower.endsWith('.hwp') || lower.endsWith('.hwpx') || ft.includes('hwp')) {
    return '한글(HWP) 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 한글 프로그램으로 열어주세요.';
  }
  if (lower.endsWith('.docx') || lower.endsWith('.doc') || ft.includes('msword') || ft.includes('wordprocessingml')) {
    return 'Word 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 Word/Pages로 열어주세요.';
  }
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || ft.includes('spreadsheetml') || ft.includes('excel')) {
    return 'Excel 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 Excel/Numbers로 열어주세요.';
  }
  if (lower.endsWith('.pptx') || lower.endsWith('.ppt') || ft.includes('presentationml') || ft.includes('powerpoint')) {
    return 'PowerPoint 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 PowerPoint/Keynote로 열어주세요.';
  }
  return '이 파일은 브라우저에서 직접 미리보기를 지원하지 않아요. 다운로드해서 확인해주세요.';
}

const getResultTotalScore = (result) => {
  if (Array.isArray(result?.question_results) && result.question_results.length > 0) {
    return result.question_results.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  if (Array.isArray(result?.item_scores) && result.item_scores.length > 0) {
    return result.item_scores.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  return result?.total_score ?? result?.score ?? 0;
};

const getRubricItemName = (item) => (
  item?.item_name ||
  item?.name ||
  item?.title ||
  item?.criteria ||
  item?.evaluation_item ||
  item?.category ||
  '평가요소'
);

const getRubricItemMaxScore = (item) => {
  const candidates = [
    item?.max_score,
    item?.max_points,
    item?.points,
    item?.point,
    item?.score,
  ];
  const value = candidates.find(v => v !== undefined && v !== null && v !== '');
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getRubricTotalPoints = (rubric) => {
  const explicitTotal = Number(rubric?.total_points || rubric?.total_score || rubric?.max_score || 0);
  if (Number.isFinite(explicitTotal) && explicitTotal > 0) return explicitTotal;
  const items = Array.isArray(rubric?.evaluation_items) ? rubric.evaluation_items : [];
  return items.reduce((sum, item) => sum + getRubricItemMaxScore(item), 0);
};

const isRoomScoringRecord = (record) => (
  record?.type === 'room' ||
  (typeof record?.type === 'string' && record.type.startsWith('multi-question-room'))
);

const isBatchScoringRecord = (record) => (
  isRoomScoringRecord(record) ||
  record?.type === 'batch' ||
  (typeof record?.type === 'string' && (
    record.type.startsWith('batch') ||
    record.type.startsWith('multi-question-batch')
  ))
);

const MAX_HISTORY_EXPORT_RECORDS = 30;
const MAX_HISTORY_EXPORT_ROWS = 300;
const HISTORY_PAGE_SIZE = 30;
const MAX_LOADED_HISTORY_RECORDS = 300;

const estimateScoringExportRows = (records) => records.reduce((sum, record) => (
  sum + Math.max(1, Number(record?.student_count || 1))
), 0);

// ── 날짜 그룹 헬퍼 ──
const getDateGroup = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (date >= today) return '오늘';
  if (date >= yesterday) return '어제';
  if (date >= weekAgo) return '이번 주';
  if (date >= monthStart) return '이번 달';
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
};

// ── 년도/학기 필터 헬퍼 ──
const getCalendarYear = (dateStr) => new Date(dateStr).getFullYear();

const isInSemesterRange = (dateStr, year, semester) => {
  const date = new Date(dateStr);
  if (semester === 1) {
    const start = new Date(year, 2, 1);
    const end = new Date(year, 8, 0, 23, 59, 59);
    return date >= start && date <= end;
  } else {
    const start = new Date(year, 8, 1);
    const end = new Date(year + 1, 2, 0, 23, 59, 59);
    return date >= start && date <= end;
  }
};

const ScoringHistory = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus } = useSubscription();

  const { showToast } = useToast();

  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [expandedEditRecordIds, setExpandedEditRecordIds] = useState([]);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('전체');
  const [selectedSemester, setSelectedSemester] = useState('전체');
  const [selectedSubject, setSelectedSubject] = useState('전체');

  // Part 2: 일괄 채점 학생별 아코디언
  const [expandedStudentIdx, setExpandedStudentIdx] = useState({});

  // Part 2-B: 인라인 편집
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemDraft, setEditingItemDraft] = useState({ score: '', reason: '', matched_level: '' });
  const [aiRefineTarget, setAiRefineTarget] = useState(null);
  const [aiRefineInstruction, setAiRefineInstruction] = useState('');
  const [aiRefineLoading, setAiRefineLoading] = useState(false);
  const [aiRefinePreview, setAiRefinePreview] = useState(null);
  const [aiRefineApplying, setAiRefineApplying] = useState(false);

  // Part 2-C: PDF 다운로드
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Part 2-D: 채점 기록 제목 편집
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [savingTitleId, setSavingTitleId] = useState(null);

  // Part 3: 탭 및 제출방 관리
  const [activeTab, setActiveTab] = useState('history');
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [expandedRoomId, setExpandedRoomId] = useState(null);
  const [roomSubmissions, setRoomSubmissions] = useState({});
  const [roomPreview, setRoomPreview] = useState(null);
  const [roomPreviewUrl, setRoomPreviewUrl] = useState(null);
  const [roomPreviewZoomed, setRoomPreviewZoomed] = useState(false);
  const [showRoomDeleteConfirm, setShowRoomDeleteConfirm] = useState(null);
  const [showQrFullscreen, setShowQrFullscreen] = useState(null);

  const isProUser = subscriptionStatus?.isPro || false;

  const getRoomSubmitUrl = useCallback((code) => {
    if (!code) return '';
    return `${window.location.origin}/submit/${code}`;
  }, []);

  const handleCopyRoomLink = useCallback((code) => {
    const url = getRoomSubmitUrl(code);
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => showToast('제출 링크를 복사했어요'))
      .catch(() => showToast('복사에 실패했어요'));
  }, [getRoomSubmitUrl, showToast]);

  // ── 날짜 포맷 (간결하게) ──
  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (d >= today) {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }
    if (d.getFullYear() !== now.getFullYear()) {
      return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' });
    }
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  // ── 사용 가능한 년도 목록 ──
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    historyData.forEach(r => yearSet.add(getCalendarYear(r.created_at)));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [historyData]);

  const activeReviewRecord = useMemo(() => (
    reviewModal?.recordId
      ? historyData.find(record => record.id === reviewModal.recordId)
      : null
  ), [historyData, reviewModal]);

  // ── 1차 필터: 년도/학기 + 검색 ──
  const baseFilteredData = useMemo(() => {
    let data = historyData;

    if (selectedAcademicYear !== '전체') {
      const yearNum = parseInt(selectedAcademicYear, 10);
      if (selectedSemester === '전체') {
        data = data.filter(r => getCalendarYear(r.created_at) === yearNum);
      } else {
        const semNum = parseInt(selectedSemester, 10);
        data = data.filter(r => isInSemesterRange(r.created_at, yearNum, semNum));
      }
    }

    return data;
  }, [historyData, selectedAcademicYear, selectedSemester]);

  // ── 교과 목록 (1차 필터 기준) ──
  const availableSubjects = useMemo(() => {
    const countMap = {};
    baseFilteredData.forEach(r => {
      if (r.subject) countMap[r.subject] = (countMap[r.subject] || 0) + 1;
    });
    return Object.entries(countMap).sort((a, b) => a[0].localeCompare(b[0]));
  }, [baseFilteredData]);

  // ── 2차 필터: 교과 ──
  const filteredData = useMemo(() => {
    if (selectedSubject === '전체') return baseFilteredData;
    return baseFilteredData.filter(r => r.subject === selectedSubject);
  }, [baseFilteredData, selectedSubject]);

  // ── 날짜 그룹핑 ──
  const groupedData = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    filteredData.forEach(record => {
      const group = getDateGroup(record.created_at);
      if (group !== currentGroup) {
        groups.push({ type: 'header', label: group });
        currentGroup = group;
      }
      groups.push({ type: 'record', data: record });
    });
    return groups;
  }, [filteredData]);

  // ── 데이터 로드 ──
  const loadHistory = useCallback(async (page = 1, append = false) => {
    try {
      setIsLoading(!append);
      const offset = (page - 1) * HISTORY_PAGE_SIZE;
      const response = await getScoringHistory(HISTORY_PAGE_SIZE, offset, isProUser, searchQuery.trim());
      const newData = response.records || [];
      if (append) {
        setHistoryData(prev => [...prev, ...newData]);
      } else {
        setHistoryData(newData);
      }
      setHasMore(Boolean(response.has_more) && (offset + newData.length) < MAX_LOADED_HISTORY_RECORDS);
    } catch (error) {
      console.error('히스토리 로드 실패:', error);
      showToast('기록을 불러오지 못했어요');
    } finally {
      setIsLoading(false);
    }
  }, [isProUser, searchQuery, showToast]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      setSelectedIds([]);
      loadHistory(1, false);
    }, searchQuery.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [currentUser, loadHistory, searchQuery]);

  // ── 더보기 ──
  const handleLoadMore = useCallback(() => {
    if (historyData.length >= MAX_LOADED_HISTORY_RECORDS) {
      setHasMore(false);
      showToast(`화면에서는 최대 ${MAX_LOADED_HISTORY_RECORDS.toLocaleString()}개 기록까지만 불러올 수 있어요.`, 'warning', 6000);
      return;
    }
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadHistory(nextPage, true);
  }, [currentPage, historyData.length, loadHistory, showToast]);

  useEffect(() => {
    if (activeTab === 'rooms' && currentUser && rooms.length === 0 && !roomsLoading) {
      loadRooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUser]);

  // ── 체크박스 ──
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const exportableRecords = [];
    let estimatedRows = 0;
    for (const record of filteredData) {
      const nextRows = estimatedRows + Math.max(1, Number(record?.student_count || 1));
      if (exportableRecords.length >= MAX_HISTORY_EXPORT_RECORDS || nextRows > MAX_HISTORY_EXPORT_ROWS) {
        break;
      }
      exportableRecords.push(record);
      estimatedRows = nextRows;
    }
    const targetIds = exportableRecords.map(r => r.id);
    const allSelected = targetIds.length > 0 && targetIds.every(id => selectedIds.includes(id));
    if (!allSelected && targetIds.length < filteredData.length) {
      showToast(`다운로드 안정성을 위해 최대 ${MAX_HISTORY_EXPORT_RECORDS}개 기록, ${MAX_HISTORY_EXPORT_ROWS}행까지만 선택했어요.`, 'info', 6000);
    }
    setSelectedIds(allSelected ? [] : targetIds);
  }, [filteredData, selectedIds, showToast]);

  // ── 삭제 ──
  const handleOpenDeleteConfirm = useCallback(() => {
    if (selectedIds.length === 0) { showToast('삭제할 기록을 선택해 주세요'); return; }
    setShowDeleteConfirm(true);
  }, [selectedIds, showToast]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    const count = idsToDelete.length;
    const prevData = historyData;

    // 낙관적 업데이트: UI 즉시 반영
    setHistoryData(prev => prev.filter(r => !idsToDelete.includes(r.id)));
    setSelectedIds([]);
    setShowDeleteConfirm(false);
    showToast(`${count}개 기록을 삭제했어요`);

    try {
      for (const id of idsToDelete) {
        await deleteScoringRecord(id);
      }
    } catch (error) {
      console.error('기록 삭제 실패:', error);
      setHistoryData(prevData);
      showToast('삭제 중 문제가 생겼어요. 다시 시도해 주세요');
    }
  }, [selectedIds, historyData, showToast]);

  // ── 제출방 목록 로드 ──
  const loadRooms = useCallback(async () => {
    if (!currentUser) return;
    try {
      setRoomsLoading(true);
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl('/api/scoring/room/list'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('제출방 로드 실패');
      const data = await res.json();
      setRooms(data.rooms || []);
    } catch (err) {
      console.error('제출방 로드 실패:', err);
      showToast('제출방 목록을 불러오지 못했어요');
    } finally {
      setRoomsLoading(false);
    }
  }, [currentUser, showToast]);

  // ── 제출방 제출물 로드 ──
  const loadRoomSubmissions = useCallback(async (roomId) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}/submissions`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('제출물 로드 실패');
      const data = await res.json();
      setRoomSubmissions(prev => ({ ...prev, [roomId]: data.submissions || [] }));
    } catch (err) {
      console.error('제출물 로드 실패:', err);
      setRoomSubmissions(prev => ({ ...prev, [roomId]: [] }));
    }
  }, [currentUser]);

  // ── 제출방 펼치기/접기 ──
  const toggleRoomExpand = useCallback((roomId) => {
    setExpandedRoomId(prev => prev === roomId ? null : roomId);
  }, []);

  // ── 제출방 펼침 시 제출물 로드 + 활성 방만 5초 폴링 ──
  useEffect(() => {
    if (!expandedRoomId || activeTab !== 'rooms') return;
    loadRoomSubmissions(expandedRoomId);

    const room = rooms.find(r => r.room_id === expandedRoomId);
    if (room?.status !== 'open') return;

    const id = setInterval(() => loadRoomSubmissions(expandedRoomId), 5000);
    return () => clearInterval(id);
  }, [expandedRoomId, activeTab, rooms, loadRoomSubmissions]);

  // ── 제출물 미리보기 ──
  const handleRoomPreview = useCallback(async (roomId, sub) => {
    if (!currentUser) return;
    setRoomPreview(sub);
    setRoomPreviewUrl(null);
    setRoomPreviewZoomed(false);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        getApiUrl(`/api/scoring/room/${roomId}/submissions/${sub.submission_id}/preview`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRoomPreviewUrl(data.preview_image_url || data.preview_pdf_url || data.preview_url);
      if (data.preview_image_url || data.preview_pdf_url) {
        setRoomPreview(prev => prev ? {
          ...prev,
          file_type: data.preview_image_url ? 'image/png' : 'application/pdf',
        } : prev);
      }
    } catch {
      showToast('미리보기를 불러오지 못했어요');
      setRoomPreview(null);
    }
  }, [currentUser, showToast]);

  // ── 채점 파일 미리보기 (batch/batch-scan 타입) ──
  const handleScoringFilePreview = useCallback(async (recordId, resultIndex, result) => {
    if (!currentUser) return;
    setRoomPreview({
      student_name: result.studentName || result.student_name || '학생',
      filename: result.fileName || '',
      file_type: result.file_type || '',
      pages: result.pages || '',
    });
    setRoomPreviewUrl(null);
    setRoomPreviewZoomed(false);
    try {
      const data = await getScoringFilePreview(recordId, resultIndex);
      setRoomPreviewUrl(data.preview_image_url || data.preview_pdf_url || data.preview_url);
      if (data.preview_image_url || data.preview_pdf_url || data.file_type) {
        setRoomPreview(prev => prev ? {
          ...prev,
          file_type: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : data.file_type),
        } : prev);
      }
    } catch {
      showToast('미리보기를 불러오지 못했어요');
      setRoomPreview(null);
    }
  }, [currentUser, showToast]);

  // ── 텍스트로 직접 입력한 답안 미리보기 (파일 없는 단일 채점 케이스) ──
  // History에 저장된 result.answer_text를 그대로 모달에 표시한다.
  // 파일이 없으므로 signed URL은 발급하지 않고, text_content 필드로 분기 렌더.
  const handleTextPreview = useCallback((result) => {
    const text = (result?.answer_text || '').trim();
    if (!text) {
      showToast('볼 수 있는 답안 텍스트가 없어요');
      return;
    }
    setRoomPreview({
      student_name: result.studentName || result.student_name || '학생',
      filename: '학생이 입력한 답안',
      file_type: 'text/plain',
      text_content: text,
    });
    setRoomPreviewUrl('text');
    setRoomPreviewZoomed(false);
  }, [showToast]);

  const getHistoryWorkspaceMatchedSubmission = useCallback((record, result) => {
    if (!isRoomScoringRecord(record) || !record?.room_id) return null;
    const subs = roomSubmissions[record.room_id];
    const studentName = getScoringStudentName(result);
    return subs?.find(
      sub => normalizeStudentNameForMatch(sub.student_name) === normalizeStudentNameForMatch(studentName)
    ) || null;
  }, [roomSubmissions]);

  const getHistoryWorkspaceSubmissionLabel = useCallback((record, result) => {
    const matchedSub = getHistoryWorkspaceMatchedSubmission(record, result);
    if (matchedSub?.filename) return matchedSub.filename;
    if (result?.fileName || result?.filename) return result.fileName || result.filename;
    if (result?.pages) return `스캔 PDF p.${result.pages}`;
    if (result?.answer_text) return '직접 입력한 답안';
    return '제출물';
  }, [getHistoryWorkspaceMatchedSubmission]);

  const buildHistoryWorkspaceResults = useCallback((record) => {
    const results = Array.isArray(record?.results) ? record.results : [];
    const cached = Array.isArray(record?.cached_answer_texts) ? record.cached_answer_texts : [];
    const matchedCached = mergeCachedAnswerTextsWithResults(cached, [], results);
    return results.map((result, index) => ({
      ...result,
      answer_text: result?.answer_text || matchedCached[index]?.answer_text || '',
    }));
  }, []);

  const loadHistoryWorkspaceSubmissionPreview = useCallback(async (record, result, resultIndex) => {
    const matchedSub = getHistoryWorkspaceMatchedSubmission(record, result);
    if (matchedSub) {
      if (!currentUser || !record?.room_id) return null;
      const token = await currentUser.getIdToken();
      const res = await fetch(
        getApiUrl(`/api/scoring/room/${record.room_id}/submissions/${matchedSub.submission_id}/preview`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('preview failed');
      const data = await res.json();
      return {
        previewUrl: data.preview_image_url || data.preview_pdf_url || data.preview_url,
        previewHtml: data.preview_html || '',
        fileType: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : (data.file_type || matchedSub.file_type || '')),
        filename: matchedSub.filename || '제출물',
        pages: matchedSub.pages || '',
      };
    }

    if (result?.storage_path) {
      if (!currentUser) return null;
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl('/api/scoring/preview-by-path'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ storage_path: result.storage_path }),
      });
      if (!res.ok) throw new Error('preview failed');
      const data = await res.json();
      return {
        previewUrl: data.preview_image_url || data.preview_pdf_url || data.preview_url,
        previewHtml: data.preview_html || '',
        fileType: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : (data.file_type || result.file_type || '')),
        filename: result.fileName || result.filename || '제출물',
        pages: result.pages || '',
      };
    }

    return null;
  }, [currentUser, getHistoryWorkspaceMatchedSubmission]);

  // ── 제출방 삭제 ──
  const handleDeleteRoom = useCallback(async (roomId) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setRooms(prev => prev.filter(r => r.room_id !== roomId));
      setShowRoomDeleteConfirm(null);
      if (expandedRoomId === roomId) setExpandedRoomId(null);
      showToast('제출방을 삭제했어요');
    } catch {
      showToast('삭제에 실패했어요');
    }
  }, [currentUser, expandedRoomId, showToast]);

  // ── 제출방 닫기/재오픈 ──
  const handleToggleRoomStatus = useCallback(async (roomId, currentStatus) => {
    if (!currentUser) return;
    const action = currentStatus === 'open' ? 'close' : 'reopen';
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}/${action}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setRooms(prev => prev.map(r =>
        r.room_id === roomId ? { ...r, status: action === 'close' ? 'closed' : 'open' } : r
      ));
      showToast(action === 'close' ? '제출방을 마감했어요' : '제출방을 다시 열었어요');
    } catch {
      showToast('처리에 실패했어요');
    }
  }, [currentUser, showToast]);

  // ── 카드 확장/축소 ──
  const toggleExpand = useCallback(async (recordId) => {
    if (expandedIds.includes(recordId)) {
      setExpandedIds(prev => prev.filter(id => id !== recordId));
      return;
    }

    const record = historyData.find(r => r.id === recordId);
    if (record?.results) {
      setExpandedIds(prev => [...prev, recordId]);
      if (isRoomScoringRecord(record) && record.room_id && !roomSubmissions[record.room_id]) {
        loadRoomSubmissions(record.room_id);
      }
    } else {
      try {
        const response = await getScoringRecord(recordId);
        setHistoryData(prev => prev.map(r =>
          r.id === recordId ? { ...r, ...response } : r
        ));
        setExpandedIds(prev => [...prev, recordId]);
        if (isRoomScoringRecord(response) && response.room_id && !roomSubmissions[response.room_id]) {
          loadRoomSubmissions(response.room_id);
        }
      } catch (error) {
        console.error('상세 정보 로드 실패:', error);
        showToast('상세 정보를 불러오지 못했어요');
      }
    }
  }, [expandedIds, historyData, showToast, roomSubmissions, loadRoomSubmissions]);

  const toggleEditPanel = useCallback((record) => {
    if (!record?.id) return;
    const isOpen = expandedEditRecordIds.includes(record.id);
    if (!isOpen && record.results?.length > 1) {
      // 수정 패널을 열 때 첫 학생 아코디언을 기본으로 펼쳐 바로 수정할 수 있게 한다
      setExpandedStudentIdx(current => (
        current[record.id] === undefined || current[record.id] === null
          ? { ...current, [record.id]: 0 }
          : current
      ));
    }
    setExpandedEditRecordIds(prev => (
      prev.includes(record.id) ? prev.filter(id => id !== record.id) : [...prev, record.id]
    ));
  }, [expandedEditRecordIds]);

  // ── 복사 ──
  const handleCopy = async (text, label = '내용') => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label}을 복사했어요`);
    } catch { showToast('복사에 실패했어요'); }
  };

  const handleStartEdit = (recordId, rIdx, field, currentValue) => {
    setEditingField({ recordId, rIdx, field });
    setEditingValue(Array.isArray(currentValue) ? currentValue.join('\n') : (currentValue || ''));
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditingValue('');
  };

  const updateRecordResultInState = useCallback((recordId, rIdx, nextResult, meta = {}) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId || !record.results) return record;
      const updatedResults = [...record.results];
      updatedResults[rIdx] = mergeScoringResultMeta(updatedResults[rIdx] || {}, nextResult || {}, rIdx);
      return {
        ...record,
        ...meta,
        results: updatedResults,
      };
    }));
  }, []);

  const handleWorkspaceSaveResult = useCallback(async (rIdx, updates) => {
    const recordId = reviewModal?.recordId;
    if (!recordId) throw new Error('채점 기록을 찾지 못했어요');
    const response = await updateScoringResult(recordId, rIdx, updates);
    if (response?.result) {
      updateRecordResultInState(recordId, rIdx, response.result, {
        total_score: response.record_total_score,
        preview: response.preview,
      });
    }
    showToast('수정이 저장되었어요');
    return response?.result;
  }, [reviewModal?.recordId, updateRecordResultInState, showToast]);

  const findRecordAndResult = useCallback((recordId, rIdx) => {
    const record = historyData.find(item => item.id === recordId);
    const result = record?.results?.[rIdx];
    return { record, result };
  }, [historyData]);

  const handleSaveEdit = async () => {
    if (!editingField) return;
    const { recordId, rIdx, field } = editingField;
    const nextValue = ['strengths', 'improvements'].includes(field)
      ? editingValue.split('\n').map(line => line.trim()).filter(Boolean)
      : editingValue;
    setIsSavingEdit(true);
    try {
      const response = await updateScoringResult(recordId, rIdx, {
        [field]: nextValue,
        revision: {
          mode: 'manual_edit',
          field,
          instruction: '교사가 직접 수정',
        },
      });
      if (response?.result) {
        updateRecordResultInState(recordId, rIdx, response.result, {
          total_score: response.record_total_score,
          preview: response.preview,
        });
      }
      showToast('수정이 저장되었어요');
      setEditingField(null);
      setEditingValue('');
    } catch {
      showToast('저장에 실패했어요. 다시 시도해 주세요', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleStartItemEdit = (recordId, rIdx, itemIndex, item) => {
    setEditingItem({ recordId, rIdx, itemIndex });
    setEditingItemDraft({
      score: item?.score ?? '',
      reason: item?.reason || '',
      matched_level: item?.matched_level || '',
    });
  };

  const handleCancelItemEdit = () => {
    setEditingItem(null);
    setEditingItemDraft({ score: '', reason: '', matched_level: '' });
  };

  const handleSaveItemEdit = async () => {
    if (!editingItem) return;
    const { recordId, rIdx, itemIndex } = editingItem;
    const { record, result } = findRecordAndResult(recordId, rIdx);
    if (!record || !result) return;

    const edit = buildItemScoreEdit(result, {
      itemIndex,
      score: editingItemDraft.score,
      reason: editingItemDraft.reason,
      matchedLevel: editingItemDraft.matched_level,
    });
    if (edit.error) {
      showToast(edit.error, 'warning');
      return;
    }

    setIsSavingEdit(true);
    try {
      const response = await updateScoringResult(recordId, rIdx, edit.updates);
      if (response?.result) {
        updateRecordResultInState(recordId, rIdx, response.result, {
          total_score: response.record_total_score,
          preview: response.preview,
        });
      }
      showToast('항목별 평가를 저장했어요');
      handleCancelItemEdit();
    } catch {
      showToast('항목별 평가 저장에 실패했어요', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const isAiTargetActive = (target) => (
    aiRefineTarget?.recordId === target.recordId
    && aiRefineTarget?.rIdx === target.rIdx
    && aiRefineTarget?.targetType === target.targetType
    && aiRefineTarget?.field === target.field
    && aiRefineTarget?.itemIndex === target.itemIndex
    && aiRefineTarget?.questionIndex === target.questionIndex
  );

  const isAiRefineTargetBlocked = (target) => (
    isSavingEdit || ((aiRefineLoading || aiRefineApplying) && !isAiTargetActive(target))
  );

  const handleOpenAiRefine = (target) => {
    if (isSavingEdit) {
      showToast('저장이 끝난 뒤 AI 수정할 수 있어요.', 'info');
      return;
    }
    if (aiRefineLoading || aiRefineApplying) {
      if (!isAiTargetActive(target)) {
        showToast('다른 AI 수정 요청이 진행 중이에요. 끝난 뒤 다시 눌러주세요.', 'info');
      }
      return;
    }
    setAiRefineTarget(target);
    setAiRefineInstruction('');
    setAiRefinePreview(null);
  };

  const handleCloseAiRefine = () => {
    setAiRefineTarget(null);
    setAiRefineInstruction('');
    setAiRefinePreview(null);
  };

  const handleRequestAiRefine = async () => {
    if (!aiRefineTarget) return;
    const instruction = aiRefineInstruction.trim();
    if (!instruction) {
      showToast('AI에게 요청할 내용을 입력해주세요', 'warning');
      return;
    }
    const maskedInstruction = instruction;

    setAiRefineLoading(true);
    setAiRefinePreview(null);
    try {
      const response = await refineScoringResult({
        record_id: aiRefineTarget.recordId,
        result_index: aiRefineTarget.rIdx,
        target_type: aiRefineTarget.targetType,
        field: aiRefineTarget.field,
        item_index: aiRefineTarget.itemIndex,
        question_index: aiRefineTarget.questionIndex,
        instruction: maskedInstruction,
      });
      setAiRefinePreview(response);
      showToast('AI 수정안을 만들었어요');
    } catch (error) {
      showToast(error.message || 'AI 수정안을 만들지 못했어요', 'error');
    } finally {
      setAiRefineLoading(false);
    }
  };

  const handleApplyAiRefinePreview = async () => {
    if (!aiRefineTarget || !aiRefinePreview?.updates) return;
    setAiRefineApplying(true);
    try {
      const response = await updateScoringResult(aiRefineTarget.recordId, aiRefineTarget.rIdx, {
        ...aiRefinePreview.updates,
        revision: aiRefinePreview.revision,
      });
      if (response?.result) {
        updateRecordResultInState(aiRefineTarget.recordId, aiRefineTarget.rIdx, response.result, {
          total_score: response.record_total_score,
          preview: response.preview,
        });
      }
      showToast('AI 수정안을 적용했어요');
      handleCloseAiRefine();
    } catch {
      showToast('AI 수정안 적용에 실패했어요', 'error');
    } finally {
      setAiRefineApplying(false);
    }
  };

  const handleStartTitleEdit = (recordId, currentTitle) => {
    setEditingTitleId(recordId);
    setEditingTitleValue(currentTitle || '');
  };

  const titleEditCancelledRef = useRef(false);

  const handleCancelTitleEdit = () => {
    titleEditCancelledRef.current = true;
    setEditingTitleId(null);
    setEditingTitleValue('');
    setTimeout(() => { titleEditCancelledRef.current = false; }, 100);
  };

  const handleSaveTitleEdit = async (recordId) => {
    if (titleEditCancelledRef.current) return;
    const nextTitle = editingTitleValue.trim();
    const prevData = historyData;
    setSavingTitleId(recordId);
    setHistoryData(prev => prev.map(r =>
      r.id === recordId ? { ...r, title: nextTitle } : r
    ));
    try {
      await updateScoringRecordMeta(recordId, { title: nextTitle });
      showToast('제목이 저장되었어요');
      setEditingTitleId(null);
      setEditingTitleValue('');
    } catch {
      setHistoryData(prevData);
      showToast('저장에 실패했어요', 'error');
    } finally {
      setSavingTitleId(null);
    }
  };

  const escHtml = useCallback((str) => {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }, []);

  const handlePdfDownload = useCallback(async (result, record, rIdx, excludeTeacherComment = true) => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    showToast('PDF를 생성하고 있어요...');
    let container = null;
    try {
      container = document.createElement('div');
      container.style.cssText = 'position:fixed;left:-9999px;top:0;width:700px;background:#fff;padding:32px;font-family:Pretendard,-apple-system,BlinkMacSystemFont,system-ui,sans-serif;';
      document.body.appendChild(container);

      const studentName = escHtml(result.studentName || result.student_name || '학생');
      const totalScore = getResultTotalScore(result);

      const buildScoreBarHtml = (itemScores) => {
        if (!itemScores?.length) return '';
        const maxTotal = itemScores.reduce((s, it) => s + (it.max_score || 0), 0);
        let barHtml = '<table style="width:100%;border-collapse:collapse;border-radius:8px;overflow:hidden;margin-bottom:20px;"><tr>';
        itemScores.forEach(item => {
          const score = item.score || 0;
          const maxScore = item.max_score || 0;
          const w = maxTotal > 0 ? (maxScore / maxTotal) * 100 : 0;
          const fill = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
          barHtml += `<td style="width:${w}%;height:32px;padding:0;background:linear-gradient(to right, #3182f6 ${fill}%, #dbeafe ${fill}%);text-align:center;white-space:nowrap;font-size:11px;font-weight:600;color:#fff;border-right:2px solid #fff;">${escHtml(item.item_name)} ${score}/${maxScore}</td>`;
        });
        barHtml += '</tr></table>';
        return barHtml;
      };

      let html = '<div>';

      html += '<div data-pdf-section>';
      html += `<table style="width:100%;margin-bottom:16px;"><tr>
        <td style="font-size:22px;font-weight:700;color:#111827;vertical-align:middle;">${studentName}</td>
        <td style="font-size:28px;font-weight:800;color:#3182f6;text-align:right;vertical-align:middle;white-space:nowrap;">${totalScore}점</td>
      </tr></table>`;
      html += buildScoreBarHtml(result.item_scores);
      html += '</div>';

      if (result.question_results?.length > 0) {
        html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">문항별 평가</div>';
        result.question_results.forEach(question => {
          const score = question.score || 0;
          const maxScore = question.max_score || 0;
          html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
            <table style="width:100%;"><tr>
              <td style="font-weight:600;font-size:13px;color:#374151;">${escHtml(question.question_title || `${question.question_id}번 문항`)}</td>
              <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${score}/${maxScore}점</td>
            </tr></table>`;
          if (question.answer_excerpt) html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;">답안: ${escHtml(question.answer_excerpt)}</div>`;
          if (question.item_scores?.length > 0) {
            question.item_scores.forEach(item => {
              html += `<div style="background:#fff;border-radius:8px;padding:8px 10px;margin-top:8px;">
                <table style="width:100%;"><tr>
                  <td style="font-weight:600;font-size:12px;color:#374151;">${escHtml(item.item_name)}</td>
                  <td style="font-weight:700;font-size:12px;color:#3182f6;text-align:right;white-space:nowrap;">${item.score || 0}/${item.max_score || 0}점</td>
                </tr></table>`;
              if (item.reason) html += `<div style="font-size:12px;color:#6b7280;line-height:1.7;margin-top:4px;">${escHtml(item.reason)}</div>`;
              html += '</div>';
            });
          }
          html += '</div>';
        });
        html += '</div>';
      }

      if (result.item_scores?.length > 0) {
        html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">항목별 평가</div>';
        result.item_scores.forEach(item => {
          const score = item.score || 0;
          const maxScore = item.max_score || 0;
          html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
            <table style="width:100%;"><tr>
              <td style="font-weight:600;font-size:13px;color:#374151;">${escHtml(item.item_name)}</td>
              <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${score}/${maxScore}점</td>
            </tr></table>`;
          if (item.matched_level) html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;"><span style="background:#e5e7eb;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;margin-right:6px;display:inline-block;">평가 기준</span>${escHtml(item.matched_level)}</div>`;
          if (item.reason) html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;"><span style="background:#dbeafe;color:#3182f6;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;margin-right:6px;display:inline-block;">평가 근거</span>${escHtml(item.reason)}</div>`;
          html += '</div>';
        });
        html += '</div>';
      }

      if (result.strengths?.length > 0 || result.improvements?.length > 0) {
        html += '<div data-pdf-section><table style="width:100%;margin-bottom:16px;border-spacing:8px;"><tr>';
        if (result.strengths?.length > 0) {
          html += '<td style="vertical-align:top;background:#f0f7ff;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#3182f6;margin-bottom:8px;">잘한 점</div><ul style="margin:0;padding-left:18px;">';
          result.strengths.forEach(s => { html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escHtml(s)}</li>`; });
          html += '</ul></td>';
        }
        if (result.improvements?.length > 0) {
          html += '<td style="vertical-align:top;background:#fffbeb;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#f59e0b;margin-bottom:8px;">보완할 점</div><ul style="margin:0;padding-left:18px;">';
          result.improvements.forEach(s => { html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escHtml(s)}</li>`; });
          html += '</ul></td>';
        }
        html += '</tr></table></div>';
      }

      if (result.detailed_feedback) {
        html += `<div data-pdf-section style="margin-bottom:16px;">
          <div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:8px;">종합 의견</div>
          <div style="background:#f9fafb;border-radius:10px;padding:14px 16px;font-size:13px;color:#374151;line-height:1.8;">${escHtml(result.detailed_feedback)}</div>
        </div>`;
      }

      if (!excludeTeacherComment && result.teacher_comment) {
        html += `<div data-pdf-section style="margin-bottom:16px;">
          <div style="font-size:14px;font-weight:700;color:#15803d;margin-bottom:8px;">교과/세특 생기부</div>
          <div style="background:#f0fdf4;border-radius:10px;padding:14px 16px;font-size:13px;color:#166534;line-height:1.8;">${escHtml(result.teacher_comment)}</div>
        </div>`;
      }

      html += '</div>';
      container.innerHTML = html;

      await new Promise(r => setTimeout(r, 100));
      const pdfBlob = await generateStudentPdf(container, studentName, { returnBlob: true });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `채점결과_${studentName || '학생'}.pdf`;
      a.click();
      showToast('다운로드 폴더에 저장되었어요', 'success', 20000, {
        label: '바로 인쇄',
        onClick: () => printPdfBlob(pdfBlob),
      });
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('PDF 생성 실패:', err);
      showToast('PDF 생성에 실패했어요', 'error');
    } finally {
      if (container && container.parentNode) document.body.removeChild(container);
      setIsGeneratingPdf(false);
    }
  }, [escHtml, isGeneratingPdf, showToast]);

  // A-4: 기록에서 바로 생기부로 이어가기 (채점 화면과 같은 batchRestore 경로)
  const handleHandoffToSaenggibu = useCallback((record) => {
    const results = Array.isArray(record?.results) ? record.results : [];
    const handoff = buildSaenggibuHandoffState({
      results,
      cachedAnswerTexts: Array.isArray(record?.cached_answer_texts) ? record.cached_answer_texts : [],
      rubric: record?.parsed_rubric || null,
      rubricTitle: record?.title || record?.subject || '',
    });
    if (!handoff) {
      showToast('생기부로 넘길 채점 내용이 없어요');
      return;
    }
    navigate('/create-saenggibu', { state: handoff });
  }, [navigate, showToast]);

  // 5-B: 선택한 채점 기록 여러 건을 학생별로 모아 교과세특 일괄 생성으로 넘긴다
  const [isPreparingHandoff, setIsPreparingHandoff] = useState(false);
  const handleMultiHandoffToSaenggibu = useCallback(async () => {
    if (selectedIds.length === 0 || isPreparingHandoff) return;
    if (selectedIds.length > SCORING_HANDOFF_MAX_RECORDS) {
      showToast(`한 번에 ${SCORING_HANDOFF_MAX_RECORDS}개 기록까지 고를 수 있어요.`, 'warning');
      return;
    }
    setIsPreparingHandoff(true);
    try {
      const handoff = await loadMultiRecordHandoff(selectedIds);
      if (!handoff) {
        showToast('생기부로 넘길 채점 내용이 없어요');
        return;
      }
      trackEvent('scoring_multi_handoff', {
        records: handoff.summary?.records || 0,
        students: handoff.summary?.students || 0,
      });
      navigate('/create-saenggibu', { state: { batchRestore: handoff.batchRestore } });
    } catch (error) {
      showToast(error?.message || '채점 기록을 불러오지 못했어요. 다시 시도해 주세요.', 'error');
    } finally {
      setIsPreparingHandoff(false);
    }
  }, [isPreparingHandoff, navigate, selectedIds, showToast]);

  const handleBulkPdfDownload = useCallback(async (record, excludeTeacherComment = true) => {
    if (isGeneratingPdf || !record.results || record.results.length === 0) return;
    const validResults = record.results.filter(r => !r.error);
    if (validResults.length === 0) { showToast('다운로드할 채점 결과가 없어요'); return; }
    setIsGeneratingPdf(true);
    showToast(`${validResults.length}명의 PDF를 생성하고 있어요...`);
    let container = null;
    try {
      const elements = [];
      const names = [];
      container = document.createElement('div');
      container.style.cssText = 'position:fixed;left:-9999px;top:0;';
      document.body.appendChild(container);

      for (const result of validResults) {
        const el = document.createElement('div');
        el.style.cssText = 'width:700px;background:#fff;padding:32px;font-family:Pretendard,-apple-system,BlinkMacSystemFont,system-ui,sans-serif;margin-bottom:20px;';
        const studentName = escHtml(result.studentName || result.student_name || '학생');

        let html = '<div>';

        html += '<div data-pdf-section>';
        html += `<table style="width:100%;margin-bottom:16px;"><tr>
          <td style="font-size:22px;font-weight:700;color:#111827;">${studentName}</td>
          <td style="font-size:28px;font-weight:800;color:#3182f6;text-align:right;white-space:nowrap;">${getResultTotalScore(result)}점</td>
        </tr></table>`;
        if (result.item_scores?.length > 0) {
          const maxTotal = result.item_scores.reduce((s, it) => s + (it.max_score || 0), 0);
          html += '<table style="width:100%;border-collapse:collapse;border-radius:8px;overflow:hidden;margin-bottom:16px;"><tr>';
          result.item_scores.forEach(item => {
            const score = item.score || 0;
            const maxScore = item.max_score || 0;
            const w = maxTotal > 0 ? (maxScore / maxTotal) * 100 : 0;
            const fill = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
            html += `<td style="width:${w}%;height:32px;padding:0;background:linear-gradient(to right, #3182f6 ${fill}%, #dbeafe ${fill}%);text-align:center;white-space:nowrap;font-size:11px;font-weight:600;color:#fff;border-right:2px solid #fff;">${escHtml(item.item_name)} ${score}/${maxScore}</td>`;
          });
          html += '</tr></table>';
        }
        html += '</div>';

        if (result.question_results?.length > 0) {
          html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">문항별 평가</div>';
          result.question_results.forEach(question => {
            const score = question.score || 0;
            const maxScore = question.max_score || 0;
            html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
              <table style="width:100%;"><tr>
                <td style="font-weight:600;font-size:13px;color:#374151;">${escHtml(question.question_title || `${question.question_id}번 문항`)}</td>
                <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${score}/${maxScore}점</td>
              </tr></table>`;
            if (question.item_scores?.length > 0) {
              question.item_scores.forEach(item => {
                html += `<div style="background:#fff;border-radius:8px;padding:8px 10px;margin-top:8px;">
                  <table style="width:100%;"><tr>
                    <td style="font-weight:600;font-size:12px;color:#374151;">${escHtml(item.item_name)}</td>
                    <td style="font-weight:700;font-size:12px;color:#3182f6;text-align:right;white-space:nowrap;">${item.score || 0}/${item.max_score || 0}점</td>
                  </tr></table>`;
                if (item.reason) html += `<div style="font-size:12px;color:#6b7280;line-height:1.7;margin-top:4px;">${escHtml(item.reason)}</div>`;
                html += '</div>';
              });
            }
            html += '</div>';
          });
          html += '</div>';
        }

        if (result.item_scores?.length > 0) {
          html += '<div data-pdf-section style="margin-bottom:16px;"><div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:10px;">항목별 평가</div>';
          result.item_scores.forEach(item => {
            const score = item.score || 0;
            const maxScore = item.max_score || 0;
            html += `<div style="background:#f9fafb;border-radius:8px;padding:10px 14px;margin-bottom:8px;">
              <table style="width:100%;"><tr>
                <td style="font-weight:600;font-size:13px;color:#374151;">${escHtml(item.item_name)}</td>
                <td style="font-weight:700;font-size:13px;color:#3182f6;text-align:right;white-space:nowrap;">${score}/${maxScore}점</td>
              </tr></table>`;
            if (item.matched_level) html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;"><span style="background:#e5e7eb;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;margin-right:6px;display:inline-block;">평가 기준</span>${escHtml(item.matched_level)}</div>`;
            if (item.reason) html += `<div style="font-size:12px;color:#6b7280;margin-top:4px;"><span style="background:#dbeafe;color:#3182f6;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;margin-right:6px;display:inline-block;">평가 근거</span>${escHtml(item.reason)}</div>`;
            html += '</div>';
          });
          html += '</div>';
        }

        if (result.strengths?.length > 0 || result.improvements?.length > 0) {
          html += '<div data-pdf-section><table style="width:100%;margin-bottom:16px;border-spacing:8px;"><tr>';
          if (result.strengths?.length > 0) {
            html += '<td style="vertical-align:top;background:#f0f7ff;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#3182f6;margin-bottom:8px;">잘한 점</div><ul style="margin:0;padding-left:18px;">';
            result.strengths.forEach(s => { html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escHtml(s)}</li>`; });
            html += '</ul></td>';
          }
          if (result.improvements?.length > 0) {
            html += '<td style="vertical-align:top;background:#fffbeb;border-radius:10px;padding:14px;width:50%;"><div style="font-size:13px;font-weight:700;color:#f59e0b;margin-bottom:8px;">보완할 점</div><ul style="margin:0;padding-left:18px;">';
            result.improvements.forEach(s => { html += `<li style="font-size:13px;color:#374151;margin-bottom:4px;line-height:1.6;">${escHtml(s)}</li>`; });
            html += '</ul></td>';
          }
          html += '</tr></table></div>';
        }

        if (result.detailed_feedback) {
          html += `<div data-pdf-section style="margin-bottom:12px;"><div style="font-weight:700;font-size:14px;margin-bottom:6px;">종합 의견</div><div style="background:#f9fafb;border-radius:8px;padding:12px;font-size:13px;line-height:1.8;">${escHtml(result.detailed_feedback)}</div></div>`;
        }

        if (!excludeTeacherComment && result.teacher_comment) {
          html += `<div data-pdf-section style="margin-bottom:12px;">
            <div style="font-weight:700;font-size:14px;color:#15803d;margin-bottom:6px;">교과/세특 생기부</div>
            <div style="background:#f0fdf4;border-radius:8px;padding:12px;font-size:13px;color:#166534;line-height:1.8;">${escHtml(result.teacher_comment)}</div>
          </div>`;
        }

        html += '</div>';
        el.innerHTML = html;
        container.appendChild(el);
        elements.push(el);
        names.push(studentName);
      }

      await new Promise(r => setTimeout(r, 200));
      const pdfType = excludeTeacherComment ? '학생용' : '교사용';
      const filename = `채점결과_${pdfType}_${record.subject || '전체'}_${names.length}명.pdf`;
      const pdfBlob = await generateBulkSinglePdf(elements, names, { returnBlob: true });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      showToast('다운로드 폴더에 저장되었어요', 'success', 20000, {
        label: '바로 인쇄',
        onClick: () => printPdfBlob(pdfBlob),
      });
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('일괄 PDF 생성 실패:', err);
      showToast('PDF 생성에 실패했어요', 'error');
    } finally {
      if (container && container.parentNode) document.body.removeChild(container);
      setIsGeneratingPdf(false);
    }
  }, [escHtml, isGeneratingPdf, showToast]);

  // ── 엑셀 다운로드 ──
  const handleExcelDownload = useCallback(async () => {
    if (selectedIds.length === 0) {
      showToast('엑셀로 받을 기록을 먼저 선택해주세요.', 'info');
      return;
    }

    const targetData = historyData.filter(r => selectedIds.includes(r.id));

    if (targetData.length === 0) { showToast('다운로드할 기록이 없어요'); return; }
    if (targetData.length > MAX_HISTORY_EXPORT_RECORDS) {
      showToast(`한 번에 최대 ${MAX_HISTORY_EXPORT_RECORDS}개 기록까지만 다운로드할 수 있어요. 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    const estimatedRows = estimateScoringExportRows(targetData);
    if (estimatedRows > MAX_HISTORY_EXPORT_ROWS) {
      showToast(`예상 ${estimatedRows.toLocaleString()}행으로 너무 커요. 학생 수나 기록 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    try {
      setIsDownloadingExcel(true);
      showToast('엑셀을 준비하고 있어요...');

      const exportResponse = await getScoringHistoryExportData(targetData.map(record => record.id));
      const detailResults = exportResponse.records || [];
      const excelData = buildScoringHistoryExportRows(detailResults);

      if (excelData.length === 0) { showToast('다운로드할 데이터가 없어요'); setIsDownloadingExcel(false); return; }

      writeRecognitionWorkbook(XLSX, excelData, {
        filename: `서술형_채점_기록_${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: '채점 기록',
      });
      showToast(`${excelData.length}개 기록이 다운로드 폴더에 저장되었어요`, 'success', 10000);
    } catch { showToast('다운로드 중 문제가 생겼어요. 다시 시도해 주세요'); }
    finally { setIsDownloadingExcel(false); }
  }, [historyData, selectedIds, showToast]);

  // ── 카드 제목 (사용자 지정 제목 우선, 없으면 기준 요약) ──
  const getCardTitle = (record) => {
    if (record.title) return record.title;
    const text = (record.rubric_preview || record.preview || '').replace(/\n+/g, ' ').trim();
    if (!text) return '채점 기준서';
    return text.length > 60 ? text.substring(0, 60) + '...' : text;
  };

  const getCardSubtitle = (record) => {
    const parts = [];
    if (record.subject) parts.push(record.subject);
    if (isRoomScoringRecord(record)) {
      parts.push(record.student_count > 1 ? `제출방 ${record.student_count}명` : '제출방');
    } else if (isBatchScoringRecord(record) && record.student_count > 1) {
      parts.push(`일괄 ${record.student_count}명`);
    } else {
      parts.push('단일 채점');
    }
    if (record.room_title) parts.push(record.room_title);
    if (record.re_score_count > 0) parts.push(`${record.re_score_count}회 조정`);
    return parts.join(' · ');
  };

  const formatScoreNumber = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value ?? 0;
    return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
  };

  const getRecordMaxScore = (record) => {
    const questionTotal = Number(record?.question_set?.total_points || 0);
    if (Number.isFinite(questionTotal) && questionTotal > 0) return questionTotal;
    return getRubricTotalPoints(record?.parsed_rubric);
  };

  const getCardScoreLabel = (record) => {
    if (isGradeOnlyRubric(record?.parsed_rubric) || (record?.results || []).some(result => isGradeOnlyResult(result, record?.parsed_rubric))) {
      const distribution = getGradeOnlyDistribution(record?.results || []);
      return isBatchScoringRecord(record) && record.student_count > 1
        ? (distribution ? `등급 분포 ${distribution}` : '등급제')
        : getGradeOnlySummary((record?.results || [])[0] || {}, '등급제');
    }
    const score = formatScoreNumber(record.total_score);
    const maxScore = getRecordMaxScore(record);
    const scoreText = maxScore > 0 ? `${score}/${formatScoreNumber(maxScore)}점` : `${score}점`;
    return isBatchScoringRecord(record) && record.student_count > 1 ? `평균 ${scoreText}` : scoreText;
  };

  // ── 교과 이니셜 ──
  const getSubjectInitial = (record) => {
    const map = { '수학': '수', '영어': '영', '과학': '과', '사회': '사', '국어': '국', '역사': '역', '도덕': '도', '음악': '음', '미술': '미', '체육': '체', '기술': '기', '정보': '정' };
    const subj = record.subject || '';
    return map[subj] || subj.charAt(0) || '채';
  };

  // ── 모델명 ──
  const getModelLabel = (model) => model === 'gemini-2.5-flash-lite' ? '빠른 모델' : '고급 모델';

  const getAiPreviewText = () => {
    if (!aiRefinePreview?.preview_result || !aiRefineTarget) return '';
    const result = aiRefinePreview.preview_result;
    if (aiRefineTarget.targetType === 'item') {
      const item = result.item_scores?.[aiRefineTarget.itemIndex];
      if (!item) return '';
      return `${item.score}/${item.max_score}점\n${item.reason || ''}`;
    }
    if (aiRefineTarget.targetType === 'field') {
      const value = result[aiRefineTarget.field];
      return Array.isArray(value) ? value.join('\n') : (value || '');
    }
    return '';
  };

  const renderAiRefinePanel = (target) => {
    if (!isAiTargetActive(target)) return null;
    const previewText = getAiPreviewText();
    return (
      <div className={`sc-ai-refine-panel ${aiRefineLoading || aiRefineApplying ? 'is-busy' : ''}`} aria-busy={aiRefineLoading || aiRefineApplying} onClick={(e) => e.stopPropagation()}>
        <p className="sc-ai-refine-title">AI에게 어떻게 고칠지 알려주세요</p>
        <p className="sc-ai-refine-subtitle">현재 항목과 채점 기준, 학생 답안, 이전 수정 이력을 함께 보고 수정안을 만들어요.</p>
        <textarea
          className="sc-edit-textarea sc-ai-refine-textarea"
          value={aiRefineInstruction}
          onChange={(e) => setAiRefineInstruction(e.target.value)}
          rows={3}
          disabled={aiRefineLoading || aiRefineApplying}
          placeholder="예: 이 항목은 근거를 더 엄격하게 보고 점수를 낮춰줘"
        />
        <div className="sc-edit-actions">
          <button className="sc-edit-cancel" onClick={handleCloseAiRefine} disabled={aiRefineLoading || aiRefineApplying}>닫기</button>
          <button className="sc-edit-save" onClick={handleRequestAiRefine} disabled={aiRefineLoading || aiRefineApplying || !aiRefineInstruction.trim()}>
            {aiRefineLoading ? '요청 중...' : 'AI에게 요청'}
          </button>
        </div>
        {aiRefineLoading && (
          <div className="sc-ai-refine-status">수정안을 만들고 있어요. 잠시만 기다려주세요.</div>
        )}
        {aiRefinePreview && (
          <div className="sc-ai-refine-preview">
            <div className="sc-ai-refine-preview-head">
              <strong>수정안</strong>
              {aiRefinePreview.revision?.summary && <span>{aiRefinePreview.revision.summary}</span>}
            </div>
            <div className="sc-ai-refine-preview-body">{previewText}</div>
            <div className="sc-edit-actions">
              <button className="sc-edit-cancel" onClick={() => setAiRefinePreview(null)} disabled={aiRefineLoading || aiRefineApplying}>다시 요청</button>
              <button className="sc-edit-save" onClick={handleApplyAiRefinePreview} disabled={aiRefineApplying}>
                {aiRefineApplying ? '적용 중...' : '수정안 적용'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── 결과 카드 렌더링 (공유 함수) ──
  const renderResultCard = (result, record, rIdx) => {
    const resultGradeOnly = isGradeOnlyResult(result, record?.parsed_rubric);
    return (
    <>
      <div className="scoring-result-header">
        <h3 className="scoring-result-student-name">
          {result.studentName || result.student_name || '채점 결과'}
        </h3>
        <div className="scoring-result-score">
          {resultGradeOnly ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`}
        </div>
      </div>

      {result.item_scores && result.item_scores.length > 0 && !resultGradeOnly && (
        <div className="scoring-points-summary-bar">
          {result.item_scores.map((item, idx) => {
            const maxTotal = result.item_scores.reduce((sum, it) => sum + (it.max_score || 0), 0);
            const widthPercent = maxTotal > 0 ? ((item.max_score || 0) / maxTotal) * 100 : 0;
            const fillPercent = item.max_score > 0 ? ((item.score || 0) / item.max_score) * 100 : 0;
            return (
              <div key={idx} className="scoring-points-bar-segment" style={{ width: `${widthPercent}%` }}>
                <div className="scoring-points-bar-fill" style={{ width: `${fillPercent}%` }} />
                <span className="scoring-points-bar-label">{item.item_name} {item.score}/{item.max_score}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 제출물 보기 — 항목별 평가 위에 배치(제출물 먼저 확인 → 채점 결과 흐름).
          ScoringPage의 일괄 채점과 동일한 흐름으로 통일.
          - room 타입: roomSubmissions에서 학생 매칭 → room API
          - room 외 + storage_path: history file preview API
          - room 외 + storage_path 없음 + answer_text: 텍스트 모달 (직접 입력 케이스) */}
      {isRoomScoringRecord(record) && record.room_id && (() => {
        const subs = roomSubmissions[record.room_id];
        const studentName = getScoringStudentName(result);
        const matchedSub = subs?.find(
          s => normalizeStudentNameForMatch(s.student_name) === normalizeStudentNameForMatch(studentName)
        );
        if (matchedSub) {
          return (
            <button
              className="sc-submission-preview-btn"
              onClick={(e) => { e.stopPropagation(); handleRoomPreview(record.room_id, matchedSub); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              제출물 보기{matchedSub.filename ? ` (${matchedSub.filename})` : ''}
            </button>
          );
        }
        if (result.storage_path) {
          return (
            <button
              className="sc-submission-preview-btn"
              onClick={(e) => { e.stopPropagation(); handleScoringFilePreview(record.id, rIdx, result); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              제출물 보기{result.fileName ? ` (${result.fileName})` : ''}
            </button>
          );
        }
        if (result.answer_text) {
          return (
            <button
              className="sc-submission-preview-btn"
              onClick={(e) => { e.stopPropagation(); handleTextPreview(result); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              제출물 보기 (직접 입력한 답안)
            </button>
          );
        }
        return null;
      })()}
      {!isRoomScoringRecord(record) && result.storage_path && (
        <button
          className="sc-submission-preview-btn"
          onClick={(e) => { e.stopPropagation(); handleScoringFilePreview(record.id, rIdx, result); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          제출물 보기{result.fileName ? ` (${result.fileName})` : ''}
          {result.pages ? ` p.${result.pages}` : ''}
        </button>
      )}
      {!isRoomScoringRecord(record) && !result.storage_path && result.answer_text && (
        <button
          className="sc-submission-preview-btn"
          onClick={(e) => { e.stopPropagation(); handleTextPreview(result); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          제출물 보기 (직접 입력한 답안)
        </button>
      )}

      {result.question_results && result.question_results.length > 0 && (
        <div className="scoring-result-section">
          <h4 className="scoring-result-section-title">문항별 평가</h4>
          <div className="scoring-question-results">
            {result.question_results.map((question, qIdx) => (
              <div key={question.question_id || qIdx} className="scoring-question-result">
                <div className="scoring-question-result-head">
                  <div>
                    <span className="scoring-question-result-kicker">{question.question_id}번 문항</span>
                    <strong>{question.question_title || `${question.question_id}번 문항`}</strong>
                  </div>
                  <span className="scoring-question-result-score">{resultGradeOnly ? (question.matched_level || getGradeOnlySummary(question)) : `${question.score || 0}/${question.max_score || 0}점`}</span>
                </div>
                {question.answer_excerpt && (
                  <p className="scoring-question-answer-excerpt">답안: {question.answer_excerpt}</p>
                )}
                {question.item_scores?.length > 0 && (
                  <div className="scoring-item-scores scoring-question-item-scores">
                    {question.item_scores.map((item, idx) => (
                      <div key={idx} className="scoring-item-score">
                        <div className="scoring-item-header">
                          <span className="scoring-item-name">{item.item_name}</span>
                          <span className="scoring-item-points">{resultGradeOnly ? (item.matched_level || '등급') : `${item.score}/${item.max_score}점`}</span>
                        </div>
                        {item.reason && (
                          <div className="scoring-criteria-match">
                            <div className="scoring-criteria-match-row scoring-criteria-match-row--ai">
                              <span className="scoring-criteria-match-tag scoring-criteria-match-tag--ai">평가 근거</span>
                              <span>{item.reason}</span>
                            </div>
                          </div>
                        )}
                        <ScoringCriterionChecks item={item} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {result.item_scores && result.item_scores.length > 0 && (
        <div className="scoring-result-section">
          <h4 className="scoring-result-section-title">항목별 평가</h4>
          <ScoringDistributionNotice result={result} />
          <div className="scoring-item-scores">
            {result.item_scores.map((item, i) => {
              const rubricItem = record.parsed_rubric?.evaluation_items?.find(
                ri => getRubricItemName(ri) === item.item_name
              );
              const matchedCriteria = rubricItem?.scoring_levels?.find(
                sl => sl.level === item.matched_level
              )?.criteria;
              const isEditingThisItem = (
                editingItem?.recordId === record.id
                && editingItem?.rIdx === rIdx
                && editingItem?.itemIndex === i
              );
              const itemAiTarget = {
                recordId: record.id,
                rIdx,
                targetType: 'item',
                itemIndex: i,
              };
              return (
                <div key={i} className="scoring-item-score">
                  <div className="scoring-item-header">
                    <div className="scoring-item-main">
                      <span className="scoring-item-name">{item.item_name}</span>
                      <span className="scoring-item-points">{resultGradeOnly ? (item.matched_level || '등급') : `${item.score}/${item.max_score}점`}</span>
                    </div>
                    <div className="scoring-item-actions">
                      <button className="sc-edit-btn" onClick={(e) => { e.stopPropagation(); handleStartItemEdit(record.id, rIdx, i, item); }}>
                        수정
                      </button>
                      <button className="sc-edit-btn" disabled={isAiRefineTargetBlocked(itemAiTarget)} onClick={(e) => { e.stopPropagation(); handleOpenAiRefine(itemAiTarget); }}>
                        AI에게 요청
                      </button>
                    </div>
                  </div>
                  {isEditingThisItem && (
                    <div className="sc-item-edit-area" onClick={(e) => e.stopPropagation()}>
                      {!resultGradeOnly && (
                        <label className="sc-item-edit-label">
                          점수
                          <input
                            className="sc-item-score-input"
                            type="number"
                            min="0"
                            max={item.max_score || undefined}
                            step="0.5"
                            value={editingItemDraft.score}
                            onChange={(e) => setEditingItemDraft(prev => ({ ...prev, score: e.target.value }))}
                          />
                        </label>
                      )}
                      {rubricItem?.scoring_levels?.length > 0 && (
                        <label className="sc-item-edit-label">
                          평가 기준
                          <select
                            className="sc-item-level-select"
                            value={editingItemDraft.matched_level}
                            onChange={(e) => setEditingItemDraft(prev => ({ ...prev, matched_level: e.target.value }))}
                          >
                            <option value="">선택 안 함</option>
                            {rubricItem.scoring_levels.map((level, levelIdx) => (
                              <option key={levelIdx} value={level.level}>
                                {level.level}{!resultGradeOnly && level.score !== undefined ? ` (${level.score}점)` : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <label className="sc-item-edit-label sc-item-edit-label--full">
                        평가 근거
                        <textarea
                          className="sc-edit-textarea"
                          value={editingItemDraft.reason}
                          onChange={(e) => setEditingItemDraft(prev => ({ ...prev, reason: e.target.value }))}
                          rows={3}
                        />
                      </label>
                      <div className="sc-edit-actions">
                        <button className="sc-edit-cancel" onClick={handleCancelItemEdit} disabled={isSavingEdit}>취소</button>
                        <button className="sc-edit-save" onClick={handleSaveItemEdit} disabled={isSavingEdit}>
                          {isSavingEdit ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                  )}
                  {item.matched_level && !matchedCriteria && (
                    <div className="scoring-criteria-match">
                      <div className="scoring-criteria-match-row">
                        <span className="scoring-criteria-match-tag">평가 기준</span>
                        <span>{item.matched_level}</span>
                      </div>
                    </div>
                  )}
                  {(matchedCriteria || item.reason) && (
                    <div className="scoring-criteria-match">
                      {matchedCriteria && (
                        <div className="scoring-criteria-match-row">
                          <span className="scoring-criteria-match-tag">평가 기준</span>
                          <span>{matchedCriteria}</span>
                        </div>
                      )}
                      {item.reason && (
                        <div className="scoring-criteria-match-row scoring-criteria-match-row--ai">
                          <span className="scoring-criteria-match-tag scoring-criteria-match-tag--ai">평가 근거</span>
                          <span>{item.reason}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <ScoringCriterionChecks item={item} />
                  {renderAiRefinePanel(itemAiTarget)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="scoring-result-feedback-grid">
        {result.strengths && result.strengths.length > 0 && (
          <div className="scoring-result-section strengths">
            <div className="sc-section-header">
              <h4 className="scoring-result-section-title">잘한 점</h4>
              <div className="sc-section-header-actions">
                <button className="sc-edit-btn" onClick={(e) => { e.stopPropagation(); handleStartEdit(record.id, rIdx, 'strengths', result.strengths); }}>
                  수정
                </button>
                <button className="sc-edit-btn" disabled={isAiRefineTargetBlocked({ recordId: record.id, rIdx, targetType: 'field', field: 'strengths' })} onClick={(e) => { e.stopPropagation(); handleOpenAiRefine({ recordId: record.id, rIdx, targetType: 'field', field: 'strengths' }); }}>
                  AI에게 요청
                </button>
              </div>
            </div>
            {editingField?.recordId === record.id && editingField?.rIdx === rIdx && editingField?.field === 'strengths' ? (
              <div className="sc-edit-area" onClick={(e) => e.stopPropagation()}>
                <textarea
                  className="sc-edit-textarea"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  rows={4}
                  autoFocus
                />
                <div className="sc-edit-actions">
                  <button className="sc-edit-cancel" onClick={handleCancelEdit} disabled={isSavingEdit}>취소</button>
                  <button className="sc-edit-save" onClick={handleSaveEdit} disabled={isSavingEdit}>
                    {isSavingEdit ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            ) : (
              <ul className="scoring-result-list">
                {result.strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
            {renderAiRefinePanel({ recordId: record.id, rIdx, targetType: 'field', field: 'strengths' })}
          </div>
        )}
        {result.improvements && result.improvements.length > 0 && (
          <div className="scoring-result-section improvements">
            <div className="sc-section-header">
              <h4 className="scoring-result-section-title">보완할 점</h4>
              <div className="sc-section-header-actions">
                <button className="sc-edit-btn" onClick={(e) => { e.stopPropagation(); handleStartEdit(record.id, rIdx, 'improvements', result.improvements); }}>
                  수정
                </button>
                <button className="sc-edit-btn" disabled={isAiRefineTargetBlocked({ recordId: record.id, rIdx, targetType: 'field', field: 'improvements' })} onClick={(e) => { e.stopPropagation(); handleOpenAiRefine({ recordId: record.id, rIdx, targetType: 'field', field: 'improvements' }); }}>
                  AI에게 요청
                </button>
              </div>
            </div>
            {editingField?.recordId === record.id && editingField?.rIdx === rIdx && editingField?.field === 'improvements' ? (
              <div className="sc-edit-area" onClick={(e) => e.stopPropagation()}>
                <textarea
                  className="sc-edit-textarea"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  rows={4}
                  autoFocus
                />
                <div className="sc-edit-actions">
                  <button className="sc-edit-cancel" onClick={handleCancelEdit} disabled={isSavingEdit}>취소</button>
                  <button className="sc-edit-save" onClick={handleSaveEdit} disabled={isSavingEdit}>
                    {isSavingEdit ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            ) : (
              <ul className="scoring-result-list">
                {result.improvements.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
            {renderAiRefinePanel({ recordId: record.id, rIdx, targetType: 'field', field: 'improvements' })}
          </div>
        )}
      </div>

      {result.detailed_feedback && (
        <div className="scoring-result-section">
          <div className="sc-section-header">
            <h4 className="scoring-result-section-title">종합 의견</h4>
            <div className="sc-section-header-actions">
              <button className="sc-edit-btn" onClick={(e) => { e.stopPropagation(); handleStartEdit(record.id, rIdx, 'detailed_feedback', result.detailed_feedback); }}>
                수정
              </button>
              <button className="sc-edit-btn" disabled={isAiRefineTargetBlocked({ recordId: record.id, rIdx, targetType: 'field', field: 'detailed_feedback' })} onClick={(e) => { e.stopPropagation(); handleOpenAiRefine({ recordId: record.id, rIdx, targetType: 'field', field: 'detailed_feedback' }); }}>
                AI에게 요청
              </button>
              <button className="sc-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(result.detailed_feedback, '총평'); }}>
                복사
              </button>
            </div>
          </div>
          {editingField?.recordId === record.id && editingField?.rIdx === rIdx && editingField?.field === 'detailed_feedback' ? (
            <div className="sc-edit-area" onClick={(e) => e.stopPropagation()}>
              <textarea
                className="sc-edit-textarea"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                rows={5}
                autoFocus
              />
              <div className="sc-edit-actions">
                <button className="sc-edit-cancel" onClick={handleCancelEdit} disabled={isSavingEdit}>취소</button>
                <button className="sc-edit-save" onClick={handleSaveEdit} disabled={isSavingEdit}>
                  {isSavingEdit ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          ) : (
            <div className="scoring-detailed-feedback">
              <p>{result.detailed_feedback}</p>
            </div>
          )}
          {renderAiRefinePanel({ recordId: record.id, rIdx, targetType: 'field', field: 'detailed_feedback' })}
        </div>
      )}

      {result.teacher_comment && (
        <div className="scoring-result-section">
          <div className="sc-section-header">
            <h4 className="scoring-result-section-title">교과/세특 생기부</h4>
            <div className="sc-section-header-actions">
              <button className="sc-edit-btn sc-edit-btn--green" onClick={(e) => { e.stopPropagation(); handleStartEdit(record.id, rIdx, 'teacher_comment', result.teacher_comment); }}>
                수정
              </button>
              <button className="sc-edit-btn sc-edit-btn--green" disabled={isAiRefineTargetBlocked({ recordId: record.id, rIdx, targetType: 'field', field: 'teacher_comment' })} onClick={(e) => { e.stopPropagation(); handleOpenAiRefine({ recordId: record.id, rIdx, targetType: 'field', field: 'teacher_comment' }); }}>
                AI에게 요청
              </button>
              <button className="sc-copy-btn--green sc-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(result.teacher_comment, '교과세특'); }}>
                복사
              </button>
            </div>
          </div>
          {editingField?.recordId === record.id && editingField?.rIdx === rIdx && editingField?.field === 'teacher_comment' ? (
            <div className="sc-edit-area" onClick={(e) => e.stopPropagation()}>
              <textarea
                className="sc-edit-textarea sc-edit-textarea--green"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className="sc-edit-actions">
                <button className="sc-edit-cancel" onClick={handleCancelEdit} disabled={isSavingEdit}>취소</button>
                <button className="sc-edit-save sc-edit-save--green" onClick={handleSaveEdit} disabled={isSavingEdit}>
                  {isSavingEdit ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          ) : (
            <div className="scoring-teacher-comment">
              <p>{result.teacher_comment}</p>
            </div>
          )}
          {renderAiRefinePanel({ recordId: record.id, rIdx, targetType: 'field', field: 'teacher_comment' })}
        </div>
      )}

      {/* 제출물 보기는 항목별 평가 위로 이동했음. */}

      <div className="sc-pdf-download-section">
        <button
          className="sc-pdf-download-btn"
          disabled={isGeneratingPdf}
          onClick={(e) => { e.stopPropagation(); handlePdfDownload(result, record, rIdx, true); }}
        >
          {isGeneratingPdf ? 'PDF 생성 중...' : '학생용 PDF 다운로드'}
        </button>
        <button
          className="sc-pdf-download-btn sc-pdf-download-btn--secondary"
          disabled={isGeneratingPdf}
          onClick={(e) => { e.stopPropagation(); handlePdfDownload(result, record, rIdx, false); }}
        >
          교사용 PDF (교과세특 포함)
        </button>
      </div>
    </>
    );
  };

  // ── 제출물 목록 렌더링 ──
  const renderRoomSubmissions = (roomId) => {
    const subs = roomSubmissions[roomId];
    if (!subs) {
      return <div className="sc-room-subs-loading">불러오는 중...</div>;
    }
    if (subs.length === 0) {
      return <div className="sc-room-subs-empty">제출물이 없어요</div>;
    }
    return (
      <div className="sc-room-subs-list">
        {subs.map((sub) => (
          <div
            key={sub.submission_id}
            className="sc-room-sub-item"
            onClick={(e) => { e.stopPropagation(); handleRoomPreview(roomId, sub); }}
          >
            <span className="sc-room-sub-name">{sub.student_name}</span>
            <span className="sc-room-sub-filename">{sub.filename || '파일'}</span>
            <span className="sc-room-sub-time">
              {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // ── 제출방 분류 ──
  const activeRooms = useMemo(() => {
    return rooms.filter(r => r.status === 'open');
  }, [rooms]);

  const closedRooms = useMemo(() => {
    return rooms.filter(r => r.status !== 'open');
  }, [rooms]);

  // ── 로그인 필요 ──
  if (!currentUser) {
    return (
      <div className="sh-empty">
        <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
        <h3 className="sh-empty-title">로그인이 필요해요</h3>
        <p className="sh-empty-desc">채점 기록을 보려면 먼저 로그인해 주세요</p>
        <button className="sh-empty-btn" onClick={() => window.location.href = '/auth?tab=login&redirect=/scoring-history'}>
          로그인하기
        </button>
      </div>
    );
  }

  return (
    <div className="sh-container">
      {/* 헤더 */}
      <div className="sh-header">
        <div className="sh-header-row">
          <h2 className="sh-title">
            {activeTab === 'history' ? '채점 기록' : '제출방 관리'}
            {activeTab === 'history' && historyData.length > 0 && (
              <span className="sh-title-count">
                {selectedIds.length > 0 ? `${selectedIds.length}개 선택` : `${filteredData.length}개`}
              </span>
            )}
            {activeTab === 'rooms' && rooms.length > 0 && (
              <span className="sh-title-count">{rooms.length}개</span>
            )}
          </h2>
        </div>
      </div>

      {/* 탭 */}
      <div className="sc-tab-bar">
        <button
          className={`sc-tab ${activeTab === 'history' ? 'sc-tab--active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          채점 기록
        </button>
        <button
          className={`sc-tab ${activeTab === 'rooms' ? 'sc-tab--active' : ''}`}
          onClick={() => setActiveTab('rooms')}
        >
          제출방 관리
        </button>
      </div>

      {/* ========== 채점 기록 탭 ========== */}
      {activeTab === 'history' && <>

      {/* 검색 + 년도/학기 필터 */}
      {!isLoading && historyData.length > 0 && (
        <div className="sh-search-row">
          <div className="sh-search-wrap">
            <svg className="sh-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="sh-search-input"
              type="text"
              placeholder="채점 기준, 내용으로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="sh-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
            )}
          </div>
          <div className="sh-semester-selects">
            {availableYears.length > 0 && (
              <>
                <select
                  className={`sh-select ${selectedAcademicYear !== '전체' ? 'sh-select--active' : ''}`}
                  value={selectedAcademicYear}
                  onChange={(e) => { setSelectedAcademicYear(e.target.value); setSelectedSemester('전체'); }}
                >
                  <option value="전체">전체 년도</option>
                  {availableYears.map(y => (
                    <option key={y} value={String(y)}>{y}년</option>
                  ))}
                </select>
                {selectedAcademicYear !== '전체' && (
                  <select
                    className={`sh-select ${selectedSemester !== '전체' ? 'sh-select--active' : ''}`}
                    value={selectedSemester}
                    onChange={(e) => setSelectedSemester(e.target.value)}
                  >
                    <option value="전체">전체 학기</option>
                    <option value="1">1학기</option>
                    <option value="2">2학기</option>
                  </select>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 검색 결과 안내 */}
      {searchQuery.trim() && (
        <div className="sh-search-result">
          &lsquo;{searchQuery.trim()}&rsquo; 검색 결과 {filteredData.length}개
        </div>
      )}

      {/* 교과 필터 칩 */}
      {!isLoading && availableSubjects.length > 0 && (
        <div className="sh-sub-filter-chips">
          <button
            className={`sh-sub-chip ${selectedSubject === '전체' ? 'sh-sub-chip--active' : ''}`}
            onClick={() => setSelectedSubject('전체')}
          >
            전체
            <span className="sh-sub-chip-count">{baseFilteredData.length}</span>
          </button>
          {availableSubjects.map(([subj, count]) => (
            <button
              key={subj}
              className={`sh-sub-chip ${selectedSubject === subj ? 'sh-sub-chip--active' : ''}`}
              onClick={() => setSelectedSubject(subj)}
            >
              {subj}
              <span className="sh-sub-chip-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* 액션 바 */}
      {!isLoading && filteredData.length > 0 && (
        <div className="sh-actions">
          <button className="sh-action-btn sh-action-btn--select" onClick={handleToggleSelectAll}>
            {filteredData.length > 0 && filteredData.slice(0, MAX_HISTORY_EXPORT_RECORDS).every(r => selectedIds.includes(r.id)) ? '선택 해제' : '전체 선택'}
          </button>
          {selectedIds.length > 0 && (
            <button className="sh-action-btn sh-action-btn--delete" onClick={handleOpenDeleteConfirm}>
              {`삭제 (${selectedIds.length})`}
            </button>
          )}
          {selectedIds.length > 0 && (
            <button
              className="sh-action-btn sh-action-btn--saenggibu"
              data-testid="sh-multi-saenggibu-handoff"
              onClick={handleMultiHandoffToSaenggibu}
              disabled={isPreparingHandoff}
            >
              {isPreparingHandoff ? '불러오는 중...' : `교과세특 쓰기 (${selectedIds.length})`}
            </button>
          )}
          <button className="sh-action-btn sh-action-btn--excel" onClick={handleExcelDownload} disabled={isDownloadingExcel || selectedIds.length === 0}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {isDownloadingExcel ? '준비 중...' : (selectedIds.length > 0 ? `답안·결과 엑셀 (${selectedIds.length})` : '답안·결과 엑셀')}
          </button>
        </div>
      )}

      {/* 스켈레톤 로딩 */}
      {isLoading && historyData.length === 0 && (
        <div className="sh-list">
          {[1,2,3,4].map(i => (
            <div key={i} className="sh-skeleton-card">
              <div className="sh-skeleton-line sh-skeleton-line--medium" />
              <div className="sh-skeleton-line sh-skeleton-line--short" />
            </div>
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {!isLoading && historyData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">아직 기록이 없어요</h3>
          <p className="sh-empty-desc">서술형 답안을 채점하면 여기에 저장돼요.</p>
          <button className="sh-empty-btn" onClick={() => window.location.href = '/scoring'}>
            채점하러 가기
          </button>
        </div>
      )}

      {/* 필터된 결과 없음 */}
      {!isLoading && historyData.length > 0 && filteredData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">검색 결과가 없어요</h3>
          <p className="sh-empty-desc">다른 검색어나 필터를 시도해보세요.</p>
        </div>
      )}

      {/* 카드 리스트 (날짜 그룹핑) */}
      {filteredData.length > 0 && (
        <div className="sh-list">
          {groupedData.map((item, idx) => {
            if (item.type === 'header') {
              return <div key={`h-${idx}`} className="sh-date-group">{item.label}</div>;
            }
            const record = item.data;
            const isExpanded = expandedIds.includes(record.id);
            const isEditExpanded = expandedEditRecordIds.includes(record.id);
            const isSelected = selectedIds.includes(record.id);

            return (
              <div
                key={record.id}
                className={`sh-card ${isSelected ? 'sh-card--selected' : ''}`}
              >
                <div
                  className="sh-card-body"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => {
                    const sel = window.getSelection();
                    if (sel && sel.toString().length > 0) return;
                    toggleExpand(record.id);
                  }}
                  onKeyDown={(e) => {
                    // 내부 버튼(저장/취소/펼치기 등)의 키 입력까지 잡으면 버튼 동작이 막히므로 카드 자신만 처리
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(record.id);
                    }
                  }}
                >
                  <div
                    className={`sh-checkbox ${isSelected ? 'sh-checkbox--checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleSelect(record.id); }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className={`sh-card-initial sc-card-initial--${isRoomScoringRecord(record) ? 'room' : (isBatchScoringRecord(record) ? 'batch' : 'single')}`}>
                    {getSubjectInitial(record)}
                  </div>
                  <div className="sh-card-info">
                    <div className="sh-card-title-row">
                      {editingTitleId === record.id ? (
                        <span className="sh-record-title-edit" onClick={(e) => e.stopPropagation()}>
                          <input
                            className="sh-record-title-input"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') handleSaveTitleEdit(record.id);
                              if (e.key === 'Escape') handleCancelTitleEdit();
                            }}
                            placeholder="예: 2반 1학기 논술 수행평가"
                            maxLength={80}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="sh-record-title-action sh-record-title-action--save"
                            disabled={savingTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveTitleEdit(record.id);
                            }}
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            className="sh-record-title-action"
                            disabled={savingTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelTitleEdit();
                            }}
                          >
                            취소
                          </button>
                        </span>
                      ) : (
                        <>
                          <p className="sh-card-title">{getCardTitle(record)}</p>
                          <button
                            type="button"
                            className="sh-record-title-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartTitleEdit(record.id, record.title || '');
                            }}
                            title="기록 이름 수정"
                            aria-label="기록 이름 수정"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                          </button>
                        </>
                      )}
                      {(record.type === 'batch' || record.type?.startsWith?.('multi-question-batch')) && <span className="sc-type-badge sc-type-badge--batch">일괄</span>}
                      {isRoomScoringRecord(record) && <span className="sc-type-badge sc-type-badge--room">제출방</span>}
                      {record.re_score_count > 0 && <span className="sc-type-badge sc-type-badge--rescore">{record.re_score_count}회 조정</span>}
                    </div>
                    <p className="sh-card-subtitle">{getCardSubtitle(record)}</p>
                  </div>
                  <div className="sh-card-right">
                    <span className="sh-card-date">{formatDate(record.created_at)}</span>
                    <span className="sh-card-score">
                      {getCardScoreLabel(record)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`sh-card-toggle ${isExpanded ? 'sh-card-toggle--open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(record.id);
                    }}
                    aria-label={isExpanded ? '채점 기록 접기' : '채점 기록 펼치기'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {/* ── 확장 영역 ── */}
                {isExpanded && record.results && (
                  <div className="sc-expanded" onClick={(e) => e.stopPropagation()}>
                    <div className="sh-expanded-meta">
                      {record.subject && <span>{record.subject}</span>}
                      {record.subject && <span className="sh-expanded-meta-sep">·</span>}
                      <span>{getCardScoreLabel(record)}</span>
                      <span className="sh-expanded-meta-sep">·</span>
                      <span>{getModelLabel(record.selected_model)}</span>
                      {record.room_title && (
                        <>
                          <span className="sh-expanded-meta-sep">·</span>
                          <span>{record.room_title}</span>
                        </>
                      )}
                    </div>

                    <div className="sc-review-launcher-head">
                      <p className="sc-review-launcher-title">학생별 결과</p>
                      <p className="sc-review-launcher-desc">학생 카드를 누르면 제출물과 채점 근거를 함께 볼 수 있어요.</p>
                    </div>
                    <ScoringReviewLauncher
                      onSelect={(rIdx) => setReviewModal({ recordId: record.id, index: rIdx })}
                      rubric={record.parsed_rubric}
                      results={record.results}
                      answerTexts={record.cached_answer_texts}
                    />

                    <div className="sc-edit-panel-control">
                      <button
                        type="button"
                        className={`sc-edit-panel-toggle ${isEditExpanded ? 'sc-edit-panel-toggle--open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEditPanel(record);
                        }}
                        aria-expanded={isEditExpanded}
                      >
                        {isEditExpanded ? '결과 수정 닫기' : '결과 수정하기'}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="sc-edit-panel-toggle"
                        data-testid={`sc-saenggibu-handoff-${record.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHandoffToSaenggibu(record);
                        }}
                      >
                        생기부로 이어가기
                      </button>
                    </div>

                    {isEditExpanded && (
                      <div className="sc-edit-panel">
                      {/* 일괄/제출방: 전체 학생 PDF 다운로드 */}
                      {record.results.length > 1 && (
                        <div className="sc-bulk-pdf-bar">
                          <button
                            className="sc-bulk-pdf-btn"
                            disabled={isGeneratingPdf}
                            onClick={(e) => { e.stopPropagation(); handleBulkPdfDownload(record, true); }}
                          >
                            {isGeneratingPdf ? 'PDF 생성 중...' : `학생용 PDF 전체 다운 (${record.results.filter(r => !r.error).length}명)`}
                          </button>
                          <button
                            className="sc-bulk-pdf-btn sc-bulk-pdf-btn--secondary"
                            disabled={isGeneratingPdf}
                            onClick={(e) => { e.stopPropagation(); handleBulkPdfDownload(record, false); }}
                          >
                            {isGeneratingPdf ? 'PDF 생성 중...' : `교사용 PDF 전체 다운 (${record.results.filter(r => !r.error).length}명)`}
                          </button>
                        </div>
                      )}
                    {/* 일괄/제출방 채점: 학생별 아코디언 */}
                    {record.results.length > 1 ? (
                      <div className="sc-students-accordion">
                        <div className="sc-students-summary">
                          {record.results.map((result, rIdx) => {
                            const isStudentExpanded = expandedStudentIdx[record.id] === rIdx;
                            return (
                              <div key={rIdx} className="sc-student-accordion">
                                <div
                                  className={`sc-student-accordion-header ${isStudentExpanded ? 'sc-student-accordion-header--open' : ''}`}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={isStudentExpanded}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedStudentIdx(prev => ({
                                      ...prev,
                                      [record.id]: prev[record.id] === rIdx ? null : rIdx,
                                    }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setExpandedStudentIdx(prev => ({
                                      ...prev,
                                      [record.id]: prev[record.id] === rIdx ? null : rIdx,
                                    }));
                                  }}
                                >
                                  {editingField?.recordId === record.id && editingField?.rIdx === rIdx && editingField?.field === 'studentName' ? (
                                    <span className="sc-student-name-edit-inline" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        className="sc-student-name-input"
                                        value={editingValue}
                                        onChange={(e) => setEditingValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <button className="sc-student-name-save" onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }} disabled={isSavingEdit}>저장</button>
                                      <button className="sc-student-name-cancel" onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}>취소</button>
                                    </span>
                                  ) : (
                                    <>
                                      <span className="sc-student-accordion-name">
                                        {result.studentName || result.student_name || `학생 ${rIdx + 1}`}
                                      </span>
                                      <button className="sc-student-name-edit-btn" onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEdit(record.id, rIdx, 'studentName', result.studentName || result.student_name || '');
                                      }} title="이름 변경">
                                        이름 변경
                                      </button>
                                    </>
                                  )}
                                  {result.error ? (
                                    <span className="sc-student-accordion-score sc-student-accordion-score--error" title={result.error}>
                                      오류
                                    </span>
                                  ) : (
                                    <span className="sc-student-accordion-score">
                                      {getResultTotalScore(result)}점
                                    </span>
                                  )}
                                  <svg className={`sc-student-accordion-arrow ${isStudentExpanded ? 'sc-student-accordion-arrow--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6 9 12 15 18 9"/>
                                  </svg>
                                </div>
                                {isStudentExpanded && result.error && (
                                  <div className="sc-student-accordion-body">
                                    <div className="sc-student-error-detail">
                                      {result.fileName && (
                                        <span className="sc-student-error-file">{result.fileName}</span>
                                      )}
                                      <span className="sc-student-error-reason">{result.error}</span>
                                    </div>
                                  </div>
                                )}
                                {isStudentExpanded && !result.error && (
                                  <div className="sc-student-accordion-body">
                                    {renderResultCard(result, record, rIdx)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="sc-result-wrapper">
                        {record.results.map((result, rIdx) => (
                          <div key={rIdx} className="scoring-result-card">
                            {result.error ? (
                              <div className="scoring-result-error">
                                <p className="scoring-result-error-message">{result.feedback || result.error}</p>
                              </div>
                            ) : renderResultCard(result, record, rIdx)}
                          </div>
                        ))}
                      </div>
                    )}
                      </div>
                    )}

                    <div className="sc-history-sub-actions">
                      {record.rubric_text && (
                        <details className="scoring-review-detail-toggle scoring-review-detail-toggle--compact">
                          <summary>기준서 원문 보기</summary>
                          <div className="sc-rubric-section">
                            <div className="sc-rubric-header">
                              <button className="sc-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(record.rubric_text, '채점 기준서'); }}>
                                복사
                              </button>
                            </div>
                            <div className="sc-rubric-text">{record.rubric_text}</div>
                          </div>
                        </details>
                      )}

                      {/* 제출방 타입: 제출물 보기 링크 */}
                      {isRoomScoringRecord(record) && record.room_id && (
                        <button
                          className="sc-rescore-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('rooms');
                            setTimeout(() => setExpandedRoomId(record.room_id), 100);
                          }}
                        >
                          제출물 보기
                        </button>
                      )}

                      {/* 이 기준서로 다시 채점 */}
                      {record.rubric_text && (
                        <button
                          className="sc-rescore-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/scoring', {
                              state: {
                                restoreScoringRecord: true,
                                recordId: record.id,
                                recordType: record.type,
                                inputMethod: record.input_method,
                                answerStructure: record.answer_structure,
                                splitMetadata: record.split_metadata,
                                scanMetadata: record.scan_metadata,
                                rubricText: record.rubric_text,
                                parsedRubric: record.parsed_rubric,
                                questionSet: record.question_set,
                                restoredResults: record.results || [],
                                restoredCachedAnswerTexts: record.cached_answer_texts?.length > 0
                                  ? record.cached_answer_texts
                                  : mergeCachedAnswerTextsWithResults([], [], record.results || []),
                                scoringOptions: record.scoring_options,
                                resolvedSchoolLevel: record.resolved_school_level,
                                resolvedStrictness: record.resolved_strictness,
                                activeScoringPolicy: record.active_scoring_policy,
                                rescoreHistory: record.rescore_history || [],
                                reScoreCount: record.re_score_count || 0,
                              },
                            });
                          }}
                        >
                          이 기준으로 다시 채점하기
                        </button>
                      )}
                    </div>
                  </div>
                )}

              </div>
            );
          })}
          {hasMore && historyData.length >= HISTORY_PAGE_SIZE && historyData.length < MAX_LOADED_HISTORY_RECORDS && (
            <button className="sh-load-more" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? '불러오는 중...' : '더보기'}
            </button>
          )}
        </div>
      )}

      </>}

      {/* ========== 제출방 관리 탭 ========== */}
      {activeTab === 'rooms' && (
        <div className="sc-rooms-tab">
          {roomsLoading && (
            <div className="sh-list">
              {[1,2,3].map(i => (
                <div key={i} className="sh-skeleton-card">
                  <div className="sh-skeleton-line sh-skeleton-line--medium" />
                  <div className="sh-skeleton-line sh-skeleton-line--short" />
                </div>
              ))}
            </div>
          )}

          {!roomsLoading && rooms.length === 0 && (
            <div className="sh-empty">
              <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
              <h3 className="sh-empty-title">제출방이 없어요</h3>
              <p className="sh-empty-desc">채점 페이지에서 학생 제출방을 만들어보세요.</p>
              <button className="sh-empty-btn" onClick={() => navigate('/scoring')}>
                채점 페이지로
              </button>
            </div>
          )}

          {!roomsLoading && activeRooms.length > 0 && (
            <div className="sc-rooms-section">
              <h3 className="sc-rooms-section-title">활성 제출방</h3>
              <div className="sc-rooms-list">
                {activeRooms.map(room => (
                  <div key={room.room_id} className={`sc-room-card ${expandedRoomId === room.room_id ? 'sc-room-card--expanded' : ''}`}>
                    <div className="sc-room-card-header" onClick={() => toggleRoomExpand(room.room_id)}>
                      <div className="sc-room-card-info">
                        <span className="sc-room-card-title">{room.title || '제출방'}</span>
                        <div className="sc-room-card-meta">
                          <span className="sc-room-card-code">{room.code}</span>
                          <span className="sc-room-card-sep">·</span>
                          <span>{room.submission_count || 0}명 제출</span>
                        </div>
                      </div>
                      <div className="sc-room-card-status sc-room-card-status--open">활성</div>
                    </div>

                    {expandedRoomId === room.room_id && (
                      <div className="sc-room-card-body">
                        {/* QR코드 섹션 */}
                        <div className="sc-room-qr-section">
                          <div
                            className="sc-room-qr-card"
                            onClick={(e) => { e.stopPropagation(); setShowQrFullscreen(room); }}
                          >
                            <QRCodeSVG
                              value={getRoomSubmitUrl(room.code)}
                              size={120}
                              level="M"
                              bgColor="#ffffff"
                              fgColor="#191f28"
                            />
                            <span className="sc-room-qr-tap-hint">터치해서 크게 보기</span>
                          </div>
                          <div className="sc-room-qr-info">
                            <div className="sc-room-qr-code-row">
                              <span className="sc-room-qr-label">코드</span>
                              <span className="sc-room-qr-code-value">{room.code}</span>
                            </div>
                            <div className="sc-room-qr-btns">
                              <button
                                className="sc-room-qr-btn"
                                onClick={(e) => { e.stopPropagation(); handleCopyRoomLink(room.code); }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                </svg>
                                링크 복사
                              </button>
                              <button
                                className="sc-room-qr-btn sc-room-qr-btn--primary"
                                onClick={(e) => { e.stopPropagation(); setShowQrFullscreen(room); }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                                </svg>
                                QR 크게 보기
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="sc-room-card-actions">
                          <button
                            className="sc-room-action-btn sc-room-action-btn--grade"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/scoring', {
                                state: {
                                  roomId: room.room_id,
                                  roomTitle: room.title,
                                  roomCode: room.code,
                                  scoringOptions: room.scoring_options,
                                  resolvedSchoolLevel: room.resolved_school_level,
                                  resolvedStrictness: room.resolved_strictness,
                                },
                              });
                            }}
                          >
                            채점하기
                          </button>
                          <button className="sc-room-action-btn" onClick={(e) => { e.stopPropagation(); handleToggleRoomStatus(room.room_id, 'open'); }}>마감하기</button>
                          <button className="sc-room-action-btn sc-room-action-btn--danger" onClick={(e) => { e.stopPropagation(); setShowRoomDeleteConfirm(room.room_id); }}>삭제</button>
                        </div>
                        {renderRoomSubmissions(room.room_id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!roomsLoading && closedRooms.length > 0 && (
            <div className="sc-rooms-section">
              <h3 className="sc-rooms-section-title">종료된 제출방</h3>
              <div className="sc-rooms-list">
                {closedRooms.map(room => (
                  <div key={room.room_id} className={`sc-room-card sc-room-card--closed ${expandedRoomId === room.room_id ? 'sc-room-card--expanded' : ''}`}>
                    <div className="sc-room-card-header" onClick={() => toggleRoomExpand(room.room_id)}>
                      <div className="sc-room-card-info">
                        <span className="sc-room-card-title">{room.title || '제출방'}</span>
                        <div className="sc-room-card-meta">
                          <span className="sc-room-card-code">{room.code}</span>
                          <span className="sc-room-card-sep">·</span>
                          <span>{room.submission_count || 0}명 제출</span>
                          {room.created_at && (
                            <>
                              <span className="sc-room-card-sep">·</span>
                              <span>{new Date(room.created_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="sc-room-card-status sc-room-card-status--closed">종료</div>
                    </div>

                    {expandedRoomId === room.room_id && (
                      <div className="sc-room-card-body">
                        {/* 종료방 QR: 코드 + 링크복사만 컴팩트하게 */}
                        <div className="sc-room-qr-compact">
                          <span className="sc-room-qr-label">코드</span>
                          <span className="sc-room-qr-code-value">{room.code}</span>
                          <button
                            className="sc-room-qr-btn sc-room-qr-btn--sm"
                            onClick={(e) => { e.stopPropagation(); handleCopyRoomLink(room.code); }}
                          >
                            링크 복사
                          </button>
                          <button
                            className="sc-room-qr-btn sc-room-qr-btn--sm"
                            onClick={(e) => { e.stopPropagation(); setShowQrFullscreen(room); }}
                          >
                            QR 보기
                          </button>
                        </div>
                        <div className="sc-room-card-actions">
                          <button
                            className="sc-room-action-btn sc-room-action-btn--grade"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/scoring', {
                                state: {
                                  roomId: room.room_id,
                                  roomTitle: room.title,
                                  roomCode: room.code,
                                  scoringOptions: room.scoring_options,
                                  resolvedSchoolLevel: room.resolved_school_level,
                                  resolvedStrictness: room.resolved_strictness,
                                },
                              });
                            }}
                          >
                            채점하기
                          </button>
                          <button className="sc-room-action-btn" onClick={(e) => { e.stopPropagation(); handleToggleRoomStatus(room.room_id, 'closed'); }}>다시 열기</button>
                          <button className="sc-room-action-btn sc-room-action-btn--danger" onClick={(e) => { e.stopPropagation(); setShowRoomDeleteConfirm(room.room_id); }}>삭제</button>
                        </div>
                        {renderRoomSubmissions(room.room_id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* QR 전체화면 모달 */}
      {showQrFullscreen && (
        <div className="scoring-qr-fullscreen-overlay" onClick={() => setShowQrFullscreen(null)}>
          <div className="scoring-qr-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <div className="scoring-qr-fullscreen-title">{showQrFullscreen.title || '과제 제출'}</div>
            <QRCodeSVG
              value={getRoomSubmitUrl(showQrFullscreen.code)}
              size={Math.min(window.innerWidth * 0.7, 400)}
              level="M"
              bgColor="#ffffff"
              fgColor="#191f28"
            />
            <p className="scoring-qr-fullscreen-desc">QR을 스캔해서 과제를 제출하세요</p>
            <div className="sc-qr-fullscreen-actions">
              <button
                className="sc-qr-fullscreen-copy"
                onClick={() => handleCopyRoomLink(showQrFullscreen.code)}
              >
                링크 복사
              </button>
              <button className="scoring-qr-fullscreen-close" onClick={() => setShowQrFullscreen(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 제출방 삭제 확인 */}
      {showRoomDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowRoomDeleteConfirm(null)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">이 제출방을 삭제할까요?</h3>
            <p className="sh-modal-desc">제출방과 모든 제출물이 삭제돼요. 되돌릴 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowRoomDeleteConfirm(null)}>취소</button>
              <button className="sh-modal-btn sh-modal-btn--delete" onClick={() => handleDeleteRoom(showRoomDeleteConfirm)}>삭제하기</button>
            </div>
          </div>
        </div>
      )}

      {activeReviewRecord?.results && reviewModal && (
        <ScoringReviewWorkspace
          getSubmissionLabel={(result) => getHistoryWorkspaceSubmissionLabel(activeReviewRecord, result)}
          initialSelectedIndex={reviewModal.index}
          loadSubmissionPreview={(result, rIdx) => loadHistoryWorkspaceSubmissionPreview(activeReviewRecord, result, rIdx)}
          onClose={() => setReviewModal(null)}
          onSaveResult={handleWorkspaceSaveResult}
          questionSet={activeReviewRecord.question_set}
          recordId={activeReviewRecord.id}
          results={buildHistoryWorkspaceResults(activeReviewRecord)}
          rubric={activeReviewRecord.parsed_rubric}
          rubricText={activeReviewRecord.rubric_text}
          title="저장된 채점 결과를 검토하세요"
          variant="modal"
        />
      )}

      {/* 제출물 미리보기 모달 */}
      {roomPreview && (
        <div className="sh-modal-overlay sc-preview-modal-overlay" onClick={() => { setRoomPreview(null); setRoomPreviewUrl(null); setRoomPreviewZoomed(false); }}>
          <div className="sc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sc-preview-modal-header">
              <span className="sc-preview-modal-title">{roomPreview.student_name}의 제출물</span>
              {roomPreview.pages && <span className="sc-preview-modal-page">p.{roomPreview.pages}</span>}
              <button className="sc-preview-modal-close" onClick={() => { setRoomPreview(null); setRoomPreviewUrl(null); setRoomPreviewZoomed(false); }}>&times;</button>
            </div>
            <div className="sc-preview-modal-body">
              {roomPreview.text_content ? (
                // 직접 입력한 답안(파일 없음) — 텍스트 그대로 표시
                <div className="scoring-preview-text-body">
                  <pre className="scoring-preview-text-pre">{roomPreview.text_content}</pre>
                </div>
              ) : !roomPreviewUrl ? (
                <div className="sc-preview-loading">불러오는 중...</div>
              ) : roomPreview.file_type?.startsWith('image/') ? (
                <img
                  src={roomPreviewUrl}
                  alt={roomPreview.student_name}
                  className={`sc-preview-image ${roomPreviewZoomed ? 'is-zoomed' : ''}`}
                  title={roomPreviewZoomed ? '클릭하면 화면에 맞게 보기' : '클릭하면 원본 크기로 확대'}
                  onClick={() => setRoomPreviewZoomed((prev) => !prev)}
                />
              ) : roomPreview.file_type === 'application/pdf' ? (
                <iframe
                  src={buildPdfPreviewSrc(roomPreviewUrl, roomPreview.pages)}
                  title="PDF 미리보기"
                  className="sc-preview-iframe"
                />
              ) : roomPreview.file_type?.startsWith('text/') ? (
                <iframe src={roomPreviewUrl} title="텍스트 미리보기" className="sc-preview-iframe" />
              ) : (
                // HWP/HWPX/DOCX/XLSX/PPTX 등 — 친절한 다운로드 안내
                <div className="sc-preview-fallback">
                  <p className="sc-preview-fallback-name">{roomPreview.filename}</p>
                  <p className="sc-preview-fallback-hint">{getHistoryFallbackHint(roomPreview.filename, roomPreview.file_type)}</p>
                  <div className="sc-preview-fallback-actions">
                    <a href={roomPreviewUrl} target="_blank" rel="noopener noreferrer" className="sc-preview-download">새 탭에서 열기</a>
                    <a href={roomPreviewUrl} download={roomPreview.filename || ''} className="sc-preview-download sc-preview-download--primary">다운로드</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 - UX Writing: 쉬운 말, 해요체 */}
      {showDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">
              {selectedIds.length === 1 ? '이 기록을 삭제할까요?' : `${selectedIds.length}개 기록을 삭제할까요?`}
            </h3>
            <p className="sh-modal-desc">삭제하면 다시 복구할 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowDeleteConfirm(false)}>
                취소
              </button>
              <button className="sh-modal-btn sh-modal-btn--delete" onClick={handleDeleteSelected}>
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ScoringHistory;
